import { call, delay, ForkEffect, put, select, StrictEffect, takeLatest } from "redux-saga/effects";

import { getErrorCodes } from "@utils/ErrorMessageUtils";

import { EventModel } from "@common/domain/models/Event";
import { MeetingModel } from "@common/domain/models/Meeting";
import { EventRepository } from "@common/domain/repositories/EventRepository";
import { MeetingRepository } from "@common/domain/repositories/MeetingRepository";

import { fetchProfilesByUserIds } from "@redux/profile/actions";
import { fetchProfilesByUserIdsSaga } from "@redux/profile/sagas";
import { getProfileByUserIdSelector } from "@redux/profile/selectors";

import { ActivitiesType } from "@constants/config";

import { IFluxStandardAction } from "@store/webInterfaces";
import { eventActions, fetchEventDetailFail, fetchEventDetailSuccess, fetchRecommendedEventsFail, fetchRecommendedEventsSuccess, fetchUpcomingUserEventsFail, fetchUpcomingUserEventsSuccess, registerEventFail, registerEventSuccess } from "./actions";

export function* fetchEventDetailSaga(action: IFluxStandardAction<string>): Generator<StrictEffect, void, EventModel> {
    try {
        const eventDetailResponse: EventModel = yield call(EventRepository.fetchEventDetail, action.payload);
        yield call(fetchEventAttendeesInfo,[] , eventDetailResponse.getOrganizer());
        const attendeesId = eventDetailResponse.getRegisteredUsers();
        if (attendeesId && attendeesId.length > 0) {
            const MAX_SIZE = 20;
            const attendeeIdArrays = [];
            for (let i = 0; i < attendeesId.length; i += MAX_SIZE) {
                attendeeIdArrays.push(attendeesId.slice(i, i + MAX_SIZE));
            }
            for (let j = 0; j < attendeeIdArrays.length; j++) {
                yield call(fetchEventAttendeesInfo, attendeeIdArrays[j]);
            }
        }
        const mappedAttendeesToEvents = yield call(mapAttendeesToEvents, eventDetailResponse);
        yield put(fetchEventDetailSuccess(mappedAttendeesToEvents));
    } catch (e) {
        const error = getErrorCodes(e);
        yield put(fetchEventDetailFail(error));
    }
}

export function* mapAttendeesToEvents(event: EventModel) {
    let attendeeArray = [];
    for (let i = 0; i < event.getRegisteredUsers().length; i++) {
      const profile = yield select(getProfileByUserIdSelector, event.getRegisteredUsers()[i]);
      attendeeArray.push(profile);
    }
    const organizerProfile = yield select(getProfileByUserIdSelector, event.getOrganizer());
    event.setAttendeesList(attendeeArray);
    event.setOrganisers(organizerProfile);
    return event;
}

export function* fetchEventAttendeesInfo(attendees: string[], organiser?: string): any {
    const attendeeUserId = attendees && attendees.map((attendee: any) => {
      return attendee;
    });
    if (organiser) {
        yield call(fetchProfilesByUserIdsSaga, fetchProfilesByUserIds([organiser]));
    }
    if(attendeeUserId.length > 0) {
      yield call(fetchProfilesByUserIdsSaga, fetchProfilesByUserIds(attendeeUserId));
    }
}

export function* fetchRecommendedEventsSaga(action) {
    try {
        const recommendedEvents: EventModel[] = yield call(EventRepository.fetchRecommendedEvents, action.payload);
        yield put(fetchRecommendedEventsSuccess(recommendedEvents));
    } catch(e) {
        const error = getErrorCodes(e);
        yield put(fetchRecommendedEventsFail(error));
    }
}

export function* fetchUpcomingEventsSaga(action) {
    yield delay(500);
    try {
        const response = [];
        const { startDateTime, limit, userId, endDate, key, status, clientName } = action.payload;
        let upcomingEvents: EventModel[];
        if (!key) {
            upcomingEvents = yield call(EventRepository.fetchUpcomingEvents, { startDate: startDateTime, endDate, limit, userId, status, clientName });
        }
        const upcomingMeetings: MeetingModel[] = yield call(MeetingRepository.fetchEventList, action.payload);

        for (let i = 0; i < upcomingMeetings.length; i++) {
            const responseObject = {
                id: upcomingMeetings[i].getId(),
                title: upcomingMeetings[i]?.getTitle(),
                startDateTime: upcomingMeetings[i].getStart().datetime,
                endDateTime: upcomingMeetings[i].getEnd().datetime,
                meetingLink: upcomingMeetings[i].getLocation() || upcomingMeetings[i].getZoomMeetingLink(),
                type: ActivitiesType.MEETING
            }
            response.push(responseObject);
        }
        if (!key) {
            for (let i = 0; i < upcomingEvents.length; i++) {
                const responseObject = {
                    id: upcomingEvents[i].getId(),
                    title: upcomingEvents[i]?.getTitle(),
                    startDateTime: upcomingEvents[i].getStartDateTime().datetime,
                    endDateTime: upcomingEvents[i].getEndDateTime().datetime,
                    meetingLink: upcomingEvents[i].getMeetingLink(),
                    type: ActivitiesType.WEBINAR
                }
                response.push(responseObject);
            }
        }
        
        
        // @ts-ignore
        const sortByStartDate = (a, b) => new Date(a.startDateTime) - new Date(b.startDateTime);

        yield put(fetchUpcomingUserEventsSuccess(response.sort(sortByStartDate)));
    } catch(e) {
        const error = getErrorCodes(e);
        yield put(fetchUpcomingUserEventsFail(error));
    }
}

export function* registerEventsSaga(action) {
    try {
        yield call(EventRepository.registerEvents, action.payload);
        yield put(registerEventSuccess(action.payload.eventId));
    } catch(e) {
        const error = getErrorCodes(e);
        yield put(registerEventFail(error));
    }
}

export function* watchEvent(): Generator<ForkEffect> {
    yield takeLatest(eventActions.FETCH_EVENT_DETAIL, fetchEventDetailSaga);
    yield takeLatest(eventActions.FETCH_RECOMMENDED_EVENT, fetchRecommendedEventsSaga);
    yield takeLatest(eventActions.FETCH_UPCOMING_USER_EVENTS, fetchUpcomingEventsSaga);
    yield takeLatest(eventActions.REGISTER_EVENT, registerEventsSaga);
}


